import React from "react";
import { CalendarCheck, PhoneCall, BellRing, Sun, RefreshCcw, RotateCcw, MessageSquareQuote } from "lucide-react";

const SECTIONS = [
  {
    icon: PhoneCall,
    accent: "#10B981",
    title: "1. Prima della prenotazione",
    items: [
      "Raccogli sempre cellulare ed email verificati: senza contatti corretti nessun reminder funziona.",
      "Chiedi il canale preferito (SMS, WhatsApp, email) e annotalo nella scheda.",
      "Spiega subito la policy: come confermare, come spostare, entro quando avvisare.",
    ],
    quote: "Per organizzare al meglio le visite, chiediamo conferma entro 48 ore.",
  },
  {
    icon: CalendarCheck,
    accent: "#2A9FCB",
    title: "2. Conferma appuntamento",
    items: [
      "Invia una conferma immediata con data, ora, sede e cosa portare.",
      "Includi sempre un link o un numero per spostare senza telefonare.",
      "Verifica che il paziente abbia ricevuto la conferma (stato di consegna).",
    ],
    quote: "Se non riesci a venire, puoi spostare da qui e liberare il posto per un altro paziente.",
  },
  {
    icon: BellRing,
    accent: "#C9A24B",
    title: "3. Reminder 72 / 48 / 24 ore",
    items: [
      "72 ore prima: promemoria informativo con possibilità di spostare.",
      "48 ore prima: richiesta di conferma esplicita (sì/no o link).",
      "24 ore prima: ultimo promemoria breve con orario e indirizzo.",
      "Se non arriva conferma entro 24 ore, la segreteria chiama direttamente.",
    ],
    quote: "Le ricordiamo la visita di domani alle 10:30. Conferma o sposta da questo link.",
  },
  {
    icon: Sun,
    accent: "#F43F5E",
    title: "4. Giorno della visita",
    items: [
      "Controlla in mattinata gli appuntamenti non confermati e segnalali.",
      "Tieni una lista d'attesa pronta per riempire eventuali buchi.",
      "Se il paziente è in ritardo, contattalo subito: spesso lo slot si salva.",
    ],
  },
  {
    icon: RefreshCcw,
    accent: "#10B981",
    title: "5. Recupero slot",
    items: [
      "Ogni disdetta è uno slot da rivendere: avvisa subito la lista d'attesa.",
      "Ordina la lista per urgenza clinica e per disponibilità dichiarata.",
      "Traccia quanti slot recuperi a settimana: è il numero che misura il metodo.",
    ],
    quote: "Si è liberato un posto per domani alle 9:15: se le interessa, confermi da questo link entro le 18.",
  },
  {
    icon: RotateCcw,
    accent: "#2A9FCB",
    title: "6. Dopo un No Show",
    items: [
      "Contatta il paziente lo stesso giorno, con tono gentile e senza colpevolizzare.",
      "Registra il No Show nella scheda: al secondo episodio cambia la policy (es. conferma obbligatoria).",
      "Proponi subito una nuova data: un No Show senza riprenotazione è spesso un paziente perso.",
    ],
    quote: "Non l'abbiamo vista oggi e ci dispiace: vuole riprenotare la visita? Le propongo due orari questa settimana.",
  },
];

export default function ExtraChecklist() {
  return (
    <div data-testid="extra-checklist">
      <div className="text-[11px] font-bold uppercase tracking-widest" style={{ color: "#10B981" }}>Checklist operativa</div>
      <h1 className="mt-1 text-2xl sm:text-3xl font-extrabold leading-tight" data-testid="checklist-title">Checklist anti No Show</h1>
      <p className="mt-3 text-sm text-white/70 leading-relaxed">
        Uno schema operativo da seguire in ordine: prenotazione, conferma, reminder, giorno della visita, recupero slot e gestione del No Show. Ogni sezione contiene azioni concrete e frasi pronte da usare.
      </p>

      <div className="mt-6 grid gap-3">
        {SECTIONS.map((s) => (
          <div key={s.title} className="bg-white rounded-lg p-5 sm:p-6 shadow-xl" data-testid={`checklist-section-${s.title.split(".")[0]}`}>
            <div className="flex items-center gap-2.5">
              <s.icon size={17} style={{ color: s.accent }} className="shrink-0" />
              <h2 className="text-sm sm:text-base font-extrabold text-brand-navy">{s.title}</h2>
            </div>
            <ul className="mt-3 grid gap-2">
              {s.items.map((it, i) => (
                <li key={i} className="flex items-start gap-2.5 text-sm text-slate-600 leading-relaxed">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: s.accent }} />
                  {it}
                </li>
              ))}
            </ul>
            {s.quote && (
              <div className="mt-3 flex items-start gap-2 rounded-lg px-3.5 py-2.5 border" style={{ borderColor: `${s.accent}44`, backgroundColor: `${s.accent}0D` }}>
                <MessageSquareQuote size={14} className="shrink-0 mt-0.5" style={{ color: s.accent }} />
                <p className="text-xs sm:text-sm italic text-slate-700 leading-relaxed">“{s.quote}”</p>
              </div>
            )}
          </div>
        ))}
      </div>

      <p className="mt-5 text-[11px] text-white/45 leading-relaxed">
        Materiale consultabile online: stampalo o condividilo con la segreteria come procedura interna dello studio.
      </p>
    </div>
  );
}
